import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { createClient } from "@supabase/supabase-js";
import { apiFetch } from "../lib/apiClient";
import ComprobanteModal from "./ComprobanteModal";
import { getPlantillasPorCliente } from "../lib/clientesService";
import {
  getRutaPlantillaById,
  getRutasPlantilla,
  calcularRutaPlantilla,
} from "../lib/rutasPlantillaService";
import {
  crearRuta,
  estimarFechasEstimadas,
  actualizarFechasEstimadas,
  notificarFechaEstimada,
  obtenerAnomaliasRuta,
} from "../lib/rutasService";
import { useGooglePlacesAutocomplete } from "../hooks/useGooglePlacesAutocomplete";
import { getNombreRuta } from "../lib/rutasUtils";
import { obtenerCostosOperativos } from "../lib/costosOperativosService";
import ConsolidacionRutaPanel from "./ConsolidacionRutaPanel";
import CostosOperativosPanel from "./CostosOperativosPanel";
import ModalRecurrencia from "./ModalRecurrencia";
import CreadorCarga from "./CreadorCarga";
import { UI_FEATURES } from "../lib/featureVisibility";
import Badge from "./ui/Badge";
import EmptyState from "./ui/EmptyState";
import Spinner from "./ui/Spinner";
import Pagination from "./ui/Pagination";

const COLS_TABLA_RUTAS = 6;

const FILTRO_LISTA_TODOS = "todos";
const FILTRO_LISTA_PENDIENTES = "pendientes";
const FILTRO_LISTA_ASIGNADOS = "asignados";
const FILTRO_LISTA_EN_TRANSITO = "en_transito";
const FILTRO_LISTA_ENTREGADOS = "entregados";

const ORDEN_RECIENTES = "recientes";
const ORDEN_ANTIGUOS = "antiguos";
const ORDEN_NOMBRE_AZ = "nombre_az";
const ORDEN_NOMBRE_ZA = "nombre_za";

const ESTADOS_ASIGNADOS = new Set(["ASIGNADO"]);
const ESTADOS_EN_TRANSITO = new Set([
  "EN_TRANSITO",
  "EN_DESTINO",
  "EN_CARGA",
  "EN_CAMINO_ORIGEN",
]);
const ESTADOS_ENTREGADOS = new Set(["ENTREGADO", "ENTREGADA", "COMPLETADO"]);

const MODO_PLANTILLA = "plantilla";
const MODO_MANUAL = "manual";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || "";
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
const supabase = createClient(supabaseUrl, supabaseKey);

function mensajeFilaClass(tipo) {
  if (tipo === "ok") return "lt-alert-banner lt-alert-banner--success";
  if (tipo === "warn") return "lt-alert-banner lt-alert-banner--warning";
  return "lt-alert-banner lt-alert-banner--error";
}

function MensajeFilaRuta({ mensaje }) {
  if (!mensaje?.texto) return null;
  return (
    <div className={mensajeFilaClass(mensaje.tipo)} role="status" style={{ marginTop: 8, marginBottom: 0 }}>
      {mensaje.texto}
    </div>
  );
}

const FECHAS_VACIAS = { inicio: "", fin: "", entrega: "" };

const ADVERTENCIA_DISTANCIA_VIAL =
  "No fue posible calcular la distancia. Ingrese el valor manualmente.";

/** datetime-local → ISO (UTC) para el backend */
function localDatetimeToIso(localVal) {
  if (!localVal || !String(localVal).trim()) return null;
  const d = new Date(localVal);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function parseLocalDatetime(localVal) {
  const value = String(localVal ?? "").trim();
  const matches = /^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2})$/.exec(value);
  if (!matches) return null;
  const [, year, month, day, hours, minutes] = matches;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    0,
    0,
  );
}

function formatLocalDatetime(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

function calcularEtaDesdeFechaInicio(fechaInicio, duracionMinutos) {
  if (!fechaInicio || duracionMinutos == null) return "";
  const inicio = parseLocalDatetime(fechaInicio);
  if (!inicio) return "";
  inicio.setMinutes(inicio.getMinutes() + Number(duracionMinutos));
  return formatLocalDatetime(inicio);
}

function formatLocalDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function calcularFechasEstimadasDesdeEta(eta) {
  if (!eta) return { inicio: "", fin: "", entrega: "" };
  const fecha = parseLocalDatetime(eta);
  if (!fecha) return { inicio: "", fin: "", entrega: "" };

  const entrega = formatLocalDate(fecha);
  const inicio = new Date(fecha);
  inicio.setDate(inicio.getDate() - 1);
  const fin = new Date(fecha);
  fin.setDate(fin.getDate() + 1);

  return {
    inicio: formatLocalDate(inicio),
    fin: formatLocalDate(fin),
    entrega,
  };
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-CL", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function toInputDate(val) {
  if (!val) return "";
  const s = String(val).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
}

function fechasFromRuta(ruta) {
  return {
    inicio: toInputDate(ruta?.fecha_estimada_inicio),
    fin: toInputDate(ruta?.fecha_estimada_fin),
    entrega: toInputDate(ruta?.fecha_estimada_entrega),
    distanciaKm:
      ruta?.distancia_km != null && ruta.distancia_km !== ""
        ? String(ruta.distancia_km)
        : "",
  };
}

function buildParadasPayload(paradas) {
  return (paradas || [])
    .filter((p) => String(p.direccion ?? "").trim())
    .map((p, idx) => ({
      direccion: String(p.direccion).trim(),
      orden: Number(p.orden) || idx + 1,
      latitud: p.latitud ?? null,
      longitud: p.longitud ?? null,
      es_temporal: p.es_temporal !== false,
    }))
    .sort((a, b) => a.orden - b.orden);
}
function buildEstimarPayload({ origen, destino, distanciaKm, fechaInicioIso }) {
  const payload = {};
  const o = String(origen ?? "").trim();
  const d = String(destino ?? "").trim();
  if (o) payload.origen = o;
  if (d) payload.destino = d;
  const km = String(distanciaKm ?? "").trim();
  if (km !== "") payload.distancia_km = Number(km);
  if (fechaInicioIso) payload.fecha_inicio = fechaInicioIso;
  return payload;
}

function mensajeEstimacionOk(data) {
  const km = data?.distancia_km;
  const ref = data?.fecha_referencia;
  if (data?.distancia_origen === "google_routes" && data?.duracion_minutos != null) {
    return `Distancia vial: ${km} km (~${data.duracion_minutos} min). Fechas calculadas (ref. ${ref}). Revise y guarde.`;
  }
  if (data?.distancia_origen === "manual") {
    return `Fechas recalculadas con distancia manual (${km} km, ref. ${ref}). Revise y guarde.`;
  }
  return `Estimación lista (${km} km, ref. ${ref}). Revise y guarde.`;
}

function estadoBadgeVariant(estado) {
  const e = String(estado || "").toUpperCase();
  if (["ENTREGADO", "COMPLETADO"].includes(e)) return "success";
  if (["CANCELADO", "CANCELADA"].includes(e)) return "danger";
  if (["EN_TRANSITO", "EN_DESTINO", "ASIGNADO", "EN_CARGA", "EN_CAMINO_ORIGEN"].includes(e)) return "accent";
  if (e === "PENDIENTE") return "warning";
  return "muted";
}

function normalizarEstadoRuta(estado) {
  return String(estado || "").trim().toUpperCase();
}

function etiquetaConductorRuta(ruta) {
  return (
    ruta?.conductores?.usuarios?.nombre ||
    ruta?.conductores?.nombre ||
    ruta?.conductores?.rut ||
    ""
  );
}

function fechaOrdenRuta(ruta) {
  const raw = ruta?.created_at || ruta?.fecha_inicio;
  const t = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
}

function rutaCoincideBusqueda(ruta, q) {
  if (!q) return true;
  const nombre = String(getNombreRuta(ruta)).toLowerCase();
  const cliente = String(ruta?.clientes?.nombre || "").toLowerCase();
  const conductor = String(etiquetaConductorRuta(ruta)).toLowerCase();
  const id = String(ruta?.id || "").toLowerCase();
  return (
    nombre.includes(q) ||
    cliente.includes(q) ||
    conductor.includes(q) ||
    id.includes(q)
  );
}

function rutaCoincideFiltroEstado(ruta, filtro) {
  if (filtro === FILTRO_LISTA_TODOS) return true;
  const e = normalizarEstadoRuta(ruta?.estado);
  if (filtro === FILTRO_LISTA_PENDIENTES) return e === "PENDIENTE";
  if (filtro === FILTRO_LISTA_ASIGNADOS) return ESTADOS_ASIGNADOS.has(e);
  if (filtro === FILTRO_LISTA_EN_TRANSITO) return ESTADOS_EN_TRANSITO.has(e);
  if (filtro === FILTRO_LISTA_ENTREGADOS) return ESTADOS_ENTREGADOS.has(e);
  return true;
}

function ordenarRutasLista(lista, orden) {
  const copia = [...lista];
  if (orden === ORDEN_NOMBRE_AZ) {
    return copia.sort((a, b) =>
      getNombreRuta(a).localeCompare(getNombreRuta(b), "es"),
    );
  }
  if (orden === ORDEN_NOMBRE_ZA) {
    return copia.sort((a, b) =>
      getNombreRuta(b).localeCompare(getNombreRuta(a), "es"),
    );
  }
  if (orden === ORDEN_ANTIGUOS) {
    return copia.sort((a, b) => fechaOrdenRuta(a) - fechaOrdenRuta(b));
  }
  return copia.sort((a, b) => fechaOrdenRuta(b) - fechaOrdenRuta(a));
}

function filtrarYOrdenarRutas(rutas, { search, filtroEstado, orden }) {
  const q = search.trim().toLowerCase();
  const filtradas = rutas.filter(
    (r) => rutaCoincideBusqueda(r, q) && rutaCoincideFiltroEstado(r, filtroEstado),
  );
  return ordenarRutasLista(filtradas, orden);
}

function estadoLabel(estado) {
  if (!estado) return "—";
  return String(estado).replace(/_/g, " ");
}

function truncarTexto(texto, max = 40) {
  if (!texto) return "—";
  const s = String(texto);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function estadoPagoBadgeVariant(estado) {
  const e = String(estado || "pendiente").toLowerCase();
  if (e === "pagado") return "success";
  if (e === "procesando") return "warning";
  if (e === "fallido") return "danger";
  return "muted";
}

function montoRutaDesdeListado(ruta) {
  const raw = ruta?.total_pagar ?? ruta?.costo_servicio ?? ruta?.tarifa_base_total;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function computeRentabilidadRuta(ruta) {
  const totalCobradoBruto = Math.round(
    Number(ruta.costo_servicio) > 0
      ? Number(ruta.costo_servicio)
      : Number(ruta.total_pagar || ruta.tarifa_base_total || 0),
  );
  const totalCobradoNeto = Math.round(
    Number(ruta.tarifa_base_total || 0) > 0
      ? Number(ruta.tarifa_base_total)
      : totalCobradoBruto / 1.19,
  );
  let pagoConductor = Number(ruta.pago_conductor_base_clp || 0);
  if (pagoConductor === 0) {
    const totalSlots =
      ruta.bultos?.reduce(
        (acc, b) => acc + (Number(b.cuadrados_equivalentes) || 0),
        0,
      ) || 1;
    const distKm = Number(ruta.distancia_km || 0);
    pagoConductor = 5000 + 3000 + totalSlots * 500 + distKm * 2 * 150;
  }
  const gastos = Math.round(
    Number(ruta.costo_combustible_calculado || 0) +
      Number(ruta.costo_tac_peajes_clp || 0) +
      pagoConductor,
  );
  const ganancia = Math.round(totalCobradoNeto - gastos);
  return { totalCobradoBruto, totalCobradoNeto, gastos, ganancia, pagoConductor };
}

export default function RutasActivas() {
  const [rutas, setRutas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [conductores, setConductores] = useState([]);
  const [camiones, setCamiones] = useState([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("crear");
  /** Sprint UX 5: flujo visual — lógica de «usar pedido anterior» en sprint posterior */
  const [origenPedido, setOrigenPedido] = useState("nuevo");
  const [saving, setSaving] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [erroresFormulario, setErroresFormulario] = useState({});
  const [mensajesRuta, setMensajesRuta] = useState({});
  const [fechasEdit, setFechasEdit] = useState({});
  const [anomaliasPorRuta, setAnomaliasPorRuta] = useState({});
  const [savingFechasId, setSavingFechasId] = useState(null);
  const [notifyingId, setNotifyingId] = useState(null);
  const [calculandoEstimacion, setCalculandoEstimacion] = useState(false);
  const [calculandoEstimacionRutaId, setCalculandoEstimacionRutaId] = useState(null);
  const [showRentabilidadModal, setShowRentabilidadModal] = useState(false);
  const [rutaRentabilidad, setRutaRentabilidad] = useState(null);
  const [comprobanteRutaId, setComprobanteRutaId] = useState(null);
  const [plantillasCliente, setPlantillasCliente] = useState([]);
  const [plantillaSeleccionadaId, setPlantillaSeleccionadaId] = useState("");
  const [cargandoPlantillas, setCargandoPlantillas] = useState(false);
  const [modoCreacion, setModoCreacion] = useState(MODO_PLANTILLA);
  const [rutasReutilizables, setRutasReutilizables] = useState([]);
  const [cargandoRutasReutilizables, setCargandoRutasReutilizables] = useState(false);
  const [paradas, setParadas] = useState([]);
  const [guardarComoPlantilla, setGuardarComoPlantilla] = useState(false);
  const [nombrePlantilla, setNombrePlantilla] = useState("");
  const [consolidacionAbiertaId, setConsolidacionAbiertaId] = useState(null);
  const [costosAbiertoId, setCostosAbiertoId] = useState(null);
  const [programarRecurrencia, setProgramarRecurrencia] = useState(false);
  const [recurrenciaModalOpen, setRecurrenciaModalOpen] = useState(false);
  const [rutaRecurrenciaCtx, setRutaRecurrenciaCtx] = useState(null);
  const [rutaDetalleSeleccionada, setRutaDetalleSeleccionada] = useState(null);
  const [costosDetalle, setCostosDetalle] = useState(null);
  const [loadingCostosDetalle, setLoadingCostosDetalle] = useState(false);
  const [listaPage, setListaPage] = useState(1);
  const [listaLimit, setListaLimit] = useState(10);
  const [listaSearch, setListaSearch] = useState("");
  const [listaFiltroEstado, setListaFiltroEstado] = useState(FILTRO_LISTA_TODOS);
  const [listaOrden, setListaOrden] = useState(ORDEN_RECIENTES);

  const [form, setForm] = useState({
    nombreRuta: "",
    clienteId: "",
    plantillaId: "",
    conductorId: "",
    camionId: "",
    origen: "",
    destino: "",
    fechaInicio: "",
    eta: "",
    duracionMinutos: null,
    distanciaKm: "",
    fechasEstimadas: { ...FECHAS_VACIAS },
    advertenciaEstimacion: "",
    bultosDespachos: "",
    observaciones: "",
  });

  const origenInputRef = useRef(null);
  const destinoInputRef = useRef(null);

  const { error: mapsOrigenError } = useGooglePlacesAutocomplete(origenInputRef, {
    enabled: false,
    onPlaceSelected: (address) => actualizarCampo("origen", address),
  });
  const { error: mapsDestinoError } = useGooglePlacesAutocomplete(destinoInputRef, {
    enabled: false,
    onPlaceSelected: (address) => actualizarCampo("destino", address),
  });
  const mapsError = mapsOrigenError || mapsDestinoError;

  const renderErrorFormulario = (campo) => {
    if (!erroresFormulario[campo]) return null;
    return (
      <div className="lt-alert-banner lt-alert-banner--error" role="alert" style={{ marginTop: 8, marginBottom: 0 }}>
        {erroresFormulario[campo]}
      </div>
    );
  };

  const cargarRutas = useCallback(async () => {
    setLoading(true);
    const res = await apiFetch("/api/rutas?limit=500");
    if (!res.ok) {
      setRutas([]);
      setAnomaliasPorRuta({});
      setMensaje({ tipo: "error", texto: res.error || "No se pudieron cargar los pedidos." });
      setLoading(false);
      return;
    }
    const payload = res.data;
    const lista = Array.isArray(payload) ? payload : payload?.data ?? [];
    setRutas(lista);
    setListaPage(1);
    const fechasMap = {};
    lista.forEach((r) => {
      fechasMap[r.id] = fechasFromRuta(r);
    });
    setFechasEdit(fechasMap);
    setLoading(false);

    if (lista.length > 0) {
      const anomaliasMap = {};
      await Promise.all(
        lista.map(async (ruta) => {
          const result = await obtenerAnomaliasRuta(ruta.id);
          anomaliasMap[ruta.id] = result.data || [];
        }),
      );
      setAnomaliasPorRuta(anomaliasMap);
    } else {
      setAnomaliasPorRuta({});
    }
  }, []);

  const cargarListas = useCallback(async () => {
    setListsLoading(true);
    const [cRes, coRes, caRes] = await Promise.all([
      apiFetch("/api/clientes"),
      apiFetch("/api/conductores"),
      apiFetch("/api/camiones/disponibles"),
    ]);

    if (cRes.ok) {
      const p = cRes.data;
      setClientes(Array.isArray(p) ? p : p?.data ?? []);
    } else setClientes([]);

    if (coRes.ok) {
      const p = coRes.data;
      setConductores(Array.isArray(p) ? p : p?.data ?? []);
    } else setConductores([]);

    if (caRes.ok) {
      const p = caRes.data;
      setCamiones(Array.isArray(p) ? p : p?.data ?? []);
    } else {
      const fallback = await apiFetch("/api/camiones");
      if (fallback.ok) {
        const p = fallback.data;
        setCamiones(Array.isArray(p) ? p : p?.data ?? []);
      } else setCamiones([]);
    }

    setListsLoading(false);
  }, []);

  useEffect(() => {
    cargarRutas();
    cargarListas();
  }, [cargarRutas, cargarListas]);

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "¿Está seguro de que desea eliminar este pedido? Esta acción no se puede deshacer.",
      )
    ) {
      return;
    }
    try {
      await supabase.from("historial_estados").delete().eq("ruta_id", id);
      await supabase.from("bultos").delete().eq("ruta_id", id);
      await supabase.from("cargas").delete().eq("ruta_id", id);
      const { error } = await supabase.from("rutas").delete().eq("id", id);
      if (error) {
        alert(`Error al eliminar pedido: ${error.message}`);
      } else {
        setRutas((prev) => prev.filter((r) => r.id !== id));
      }
    } catch (err) {
      alert(`Error al procesar eliminación: ${err.message}`);
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function cargarPlantillas() {
      if (!form.clienteId) {
        setPlantillasCliente([]);
        setPlantillaSeleccionadaId("");
        setForm((prev) => ({ ...prev, plantillaId: "" }));
        return;
      }

      setCargandoPlantillas(true);
      const res = await getPlantillasPorCliente(form.clienteId);
      if (cancelled) return;

      setPlantillasCliente(res.data || []);
      setPlantillaSeleccionadaId("");
      setForm((prev) => ({ ...prev, plantillaId: "" }));
      setCargandoPlantillas(false);
    }

    cargarPlantillas();
    return () => {
      cancelled = true;
    };
  }, [form.clienteId]);

  useEffect(() => {
    let cancelled = false;

    async function cargarRutasReutilizables() {
      if (modoCreacion !== MODO_PLANTILLA) {
        setRutasReutilizables([]);
        return;
      }

      setCargandoRutasReutilizables(true);
      const params = { activa: "true" };
      if (form.clienteId) params.clienteId = form.clienteId;
      const res = await getRutasPlantilla(params);
      if (cancelled) return;

      const lista = res.data?.data ?? res.data ?? [];
      setRutasReutilizables(Array.isArray(lista) ? lista : []);
      setCargandoRutasReutilizables(false);
    }

    cargarRutasReutilizables();
    return () => {
      cancelled = true;
    };
  }, [modoCreacion, form.clienteId]);

  const aplicarPlantilla = async (plantillaId) => {
    setPlantillaSeleccionadaId(plantillaId);
    if (!plantillaId) {
      setForm((prev) => ({ ...prev, plantillaId: "", eta: "", duracionMinutos: null }));
      setParadas([]);
      return;
    }

    const res = await getRutaPlantillaById(plantillaId);
    if (res.error || !res.data) {
      setMensaje({ tipo: "error", texto: res.error || "No fue posible cargar la plantilla." });
      return;
    }

    const plantilla = res.data;
    const paradasPlantilla = (plantilla.paradas || []).map((p) => ({
      direccion: p.direccion || "",
      orden: p.orden,
      latitud: p.latitud ?? null,
      longitud: p.longitud ?? null,
      es_temporal: false,
    }));
    setParadas(paradasPlantilla);
    setForm((prev) => ({
      ...prev,
      plantillaId: modoCreacion === MODO_PLANTILLA ? plantillaId : "",
      nombreRuta: plantilla.nombre || prev.nombreRuta,
      origen: plantilla.origen || prev.origen,
      destino: plantilla.destino || prev.destino,
      distanciaKm: "",
      duracionMinutos: null,
      eta: "",
      fechasEstimadas: { ...FECHAS_VACIAS },
      advertenciaEstimacion: "",
    }));
    setMensaje({
      tipo: "ok",
      texto: `Ruta «${plantilla.nombre}» cargada. Origen y destino autocompletados.`,
    });
  };

  const agregarParada = () => {
    setParadas((prev) => [
      ...prev,
      {
        direccion: "",
        orden: prev.length + 1,
        latitud: null,
        longitud: null,
        es_temporal: true,
      },
    ]);
    setForm((prev) => ({
      ...prev,
      eta: "",
      duracionMinutos: null,
    }));
  };

  const actualizarParada = (index, campo, valor) => {
    setParadas((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [campo]: valor } : p)),
    );
    setForm((prev) => ({
      ...prev,
      eta: "",
      duracionMinutos: null,
    }));
  };

  const actualizarParadaDesdePlaces = (index, datos) => {
    setParadas((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...datos, es_temporal: true } : p)),
    );
    setForm((prev) => ({
      ...prev,
      eta: "",
      duracionMinutos: null,
    }));
  };

  const eliminarParada = (index) => {
    setParadas((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((p, i) => ({ ...p, orden: i + 1 })),
    );
    setForm((prev) => ({
      ...prev,
      eta: "",
      duracionMinutos: null,
    }));
  };

  const cambiarModoCreacion = (modo) => {
    setModoCreacion(modo);
    setPlantillaSeleccionadaId("");
    setParadas([]);
    setGuardarComoPlantilla(false);
    setNombrePlantilla("");
    setForm((prev) => ({
      ...prev,
      plantillaId: "",
      origen: modo === MODO_MANUAL ? prev.origen : "",
      destino: modo === MODO_MANUAL ? prev.destino : "",
      eta: "",
      duracionMinutos: null,
      advertenciaEstimacion: "",
    }));
    setErroresFormulario({});
  };

  const actualizarCampo = (campo, valor) => {
    const update = { [campo]: valor };
    if (["origen", "destino", "distanciaKm"].includes(campo)) {
      update.eta = "";
      update.duracionMinutos = null;
    }
    if (["origen", "destino"].includes(campo)) {
      update.distanciaKm = "";
      update.fechasEstimadas = { ...FECHAS_VACIAS };
    }
    setForm((prev) => ({ ...prev, ...update }));
    setErroresFormulario((prev) => {
      if (!prev[campo]) return prev;
      const next = { ...prev };
      delete next[campo];
      return next;
    });
  };

  useEffect(() => {
    const tieneFechaInicio = String(form.fechaInicio || "").trim() !== "";
    const duracionValida = Number(form.duracionMinutos) > 0;

    if (!tieneFechaInicio || !duracionValida) {
      if (form.eta || form.fechasEstimadas.inicio || form.fechasEstimadas.fin || form.fechasEstimadas.entrega) {
        setForm((prev) => ({
          ...prev,
          eta: "",
          fechasEstimadas: { ...FECHAS_VACIAS },
        }));
      }
      return;
    }

    const nuevaEta = calcularEtaDesdeFechaInicio(
      form.fechaInicio,
      form.duracionMinutos,
    );
    if (nuevaEta !== form.eta) {
      setForm((prev) => ({ ...prev, eta: nuevaEta }));
    }
  }, [form.fechaInicio, form.duracionMinutos]);

  useEffect(() => {
    const nuevasFechas = calcularFechasEstimadasDesdeEta(form.eta);
    setForm((prev) => {
      if (
        prev.fechasEstimadas.inicio === nuevasFechas.inicio &&
        prev.fechasEstimadas.fin === nuevasFechas.fin &&
        prev.fechasEstimadas.entrega === nuevasFechas.entrega
      ) {
        return prev;
      }
      return {
        ...prev,
        fechasEstimadas: nuevasFechas,
      };
    });
  }, [form.eta]);

  const actualizarFechaForm = (campo, valor) => {
    setForm((prev) => ({
      ...prev,
      fechasEstimadas: { ...prev.fechasEstimadas, [campo]: valor },
    }));
    const errorKey =
      campo === "inicio"
        ? "fechaInicioEstimado"
        : campo === "fin"
          ? "finRangoEstimado"
          : "diaEstimadoEntrega";
    setErroresFormulario((prev) => {
      if (!prev[errorKey]) return prev;
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  };

  const calcularDistanciaYFechasForm = async () => {
    setForm((prev) => ({
      ...prev,
      advertenciaEstimacion: "",
      distanciaKm: "",
      eta: "",
      duracionMinutos: null,
      fechasEstimadas: { ...FECHAS_VACIAS },
    }));
    const tieneKm = String(form.distanciaKm ?? "").trim() !== "";
    const paradasValidas = buildParadasPayload(paradas);
    if (!tieneKm && (!form.origen.trim() || !form.destino.trim())) {
      setForm((prev) => ({
        ...prev,
        advertenciaEstimacion:
          "Indique origen y destino para calcular la distancia vial, o ingrese la distancia manualmente.",
      }));
      return;
    }

    setCalculandoEstimacion(true);
    const fi = localDatetimeToIso(form.fechaInicio);

    if (paradasValidas.length > 0 && form.origen.trim() && form.destino.trim()) {
      const resCalc = await calcularRutaPlantilla({
        origen: form.origen.trim(),
        destino: form.destino.trim(),
        paradas: paradasValidas.map((p) => ({
          direccion: p.direccion,
          orden: p.orden,
        })),
      });
      setCalculandoEstimacion(false);

      if (resCalc.error || resCalc.data?.distanciaEstimada == null) {
        setForm((prev) => ({
          ...prev,
          advertenciaEstimacion: resCalc.error || ADVERTENCIA_DISTANCIA_VIAL,
        }));
        return;
      }

      const distancia = resCalc.data.distanciaEstimada;
      const duracion = resCalc.data.tiempoEstimado ?? null;
      const resFechas = await estimarFechasEstimadas(
        buildEstimarPayload({
          origen: form.origen,
          destino: form.destino,
          distanciaKm: distancia,
          fechaInicioIso: fi,
        }),
      );

      if (!resFechas.success || !resFechas.data?.ok) {
        setForm((prev) => ({
          ...prev,
          distanciaKm: String(distancia),
          duracionMinutos: duracion,
          eta:
            duracion != null && prev.fechaInicio
              ? calcularEtaDesdeFechaInicio(prev.fechaInicio, duracion)
              : "",
          advertenciaEstimacion:
            resFechas.error || resFechas.data?.advertencia || ADVERTENCIA_DISTANCIA_VIAL,
        }));
        return;
      }

      const data = resFechas.data;
      setForm((prev) => ({
        ...prev,
        advertenciaEstimacion: "",
        distanciaKm: String(distancia),
        duracionMinutos: duracion,
        eta: duracion != null ? calcularEtaDesdeFechaInicio(form.fechaInicio, duracion) : prev.eta,
        fechasEstimadas: {
          inicio: data.fecha_estimada_inicio || "",
          fin: data.fecha_estimada_fin || "",
          entrega: data.fecha_estimada_entrega || "",
        },
      }));
      return;
    }

    const res = await estimarFechasEstimadas(
      buildEstimarPayload({
        origen: form.origen,
        destino: form.destino,
        distanciaKm: tieneKm ? form.distanciaKm : "",
        fechaInicioIso: fi,
      }),
    );
    setCalculandoEstimacion(false);

    if (!res.success) {
      setForm((prev) => ({
        ...prev,
        advertenciaEstimacion: res.error || ADVERTENCIA_DISTANCIA_VIAL,
      }));
      return;
    }

    const data = res.data;
    if (!data?.ok) {
      setForm((prev) => ({
        ...prev,
        advertenciaEstimacion: data?.advertencia || ADVERTENCIA_DISTANCIA_VIAL,
      }));
      return;
    }

    setForm((prev) => {
      const duracion = data.duracion_minutos ?? null;
      return {
        ...prev,
        advertenciaEstimacion: "",
        distanciaKm:
          data.distancia_km != null ? String(data.distancia_km) : prev.distanciaKm,
        duracionMinutos: duracion,
        eta:
          duracion != null && prev.fechaInicio
            ? calcularEtaDesdeFechaInicio(prev.fechaInicio, duracion)
            : prev.eta,
        fechasEstimadas: {
          inicio: data.fecha_estimada_inicio || "",
          fin: data.fecha_estimada_fin || "",
          entrega: data.fecha_estimada_entrega || "",
        },
      };
    });
  };

  const calcularDistanciaYFechasRuta = async (ruta) => {
    const rutaId = ruta.id;
    limpiarMensajeRuta(rutaId, "estimacion");
    const edit = fechasEdit[rutaId] || fechasFromRuta(ruta);
    const tieneKm = String(edit.distanciaKm ?? "").trim() !== "";
    if (
      !tieneKm &&
      (!String(ruta.origen || "").trim() || !String(ruta.destino || "").trim())
    ) {
      setMensajeRuta(rutaId, "estimacion", {
        tipo: "warn",
        texto:
          "La ruta no tiene origen/destino para calcular distancia vial. Ingrese distancia manualmente.",
      });
      return;
    }

    setCalculandoEstimacionRutaId(rutaId);
    const res = await estimarFechasEstimadas(
      buildEstimarPayload({
        origen: ruta.origen,
        destino: ruta.destino,
        distanciaKm: tieneKm ? edit.distanciaKm : "",
        fechaInicioIso: ruta.fecha_inicio || null,
      }),
    );
    setCalculandoEstimacionRutaId(null);

    if (!res.success) {
      setMensajeRuta(rutaId, "estimacion", {
        tipo: "warn",
        texto: res.error || ADVERTENCIA_DISTANCIA_VIAL,
      });
      return;
    }

    const data = res.data;
    if (!data?.ok) {
      setMensajeRuta(rutaId, "estimacion", {
        tipo: "warn",
        texto: data?.advertencia || ADVERTENCIA_DISTANCIA_VIAL,
      });
      return;
    }

    setFechasEdit((prev) => ({
      ...prev,
      [rutaId]: {
        inicio: data.fecha_estimada_inicio || "",
        fin: data.fecha_estimada_fin || "",
        entrega: data.fecha_estimada_entrega || "",
        distanciaKm:
          data.distancia_km != null ? String(data.distancia_km) : edit.distanciaKm,
      },
    }));
    setMensajeRuta(rutaId, "estimacion", {
      tipo: "ok",
      texto: mensajeEstimacionOk(data),
    });
  };

  const enviarFormulario = async (e) => {
    e.preventDefault();
    setMensaje(null);
    setErroresFormulario({});

    const nuevosErrores = {};

    if (modoCreacion === MODO_PLANTILLA && !form.plantillaId) {
      nuevosErrores.plantillaId = "Debe seleccionar una ruta existente";
    }

    const requeridos = [
      ["clienteId", form.clienteId],
      ["conductorId", form.conductorId],
      ["camionId", form.camionId],
      ["origen", form.origen],
      ["destino", form.destino],
      ["fechaInicio", form.fechaInicio],
      ["bultosDespachos", form.bultosDespachos],
      ["distanciaKm", form.distanciaKm],
      ["fechaInicioEstimado", form.fechasEstimadas.inicio],
      ["finRangoEstimado", form.fechasEstimadas.fin],
      ["diaEstimadoEntrega", form.fechasEstimadas.entrega],
    ];

    requeridos.forEach(([campo, valor]) => {
      if (!String(valor ?? "").trim()) {
        nuevosErrores[campo] = "Este campo es obligatorio";
      }
    });

    const bultosDespachosValue = Number(form.bultosDespachos);
    if (
      String(form.bultosDespachos ?? "").trim() &&
      (Number.isNaN(bultosDespachosValue) || !Number.isInteger(bultosDespachosValue) || bultosDespachosValue < 1)
    ) {
      nuevosErrores.bultosDespachos = "Cantidad de slots inválida";
    }

    const distanciaOriginal = String(form.distanciaKm ?? "").trim();
    const distanciaLimpia = distanciaOriginal.replace(/,/g, ".");
    const distanciaNumero = distanciaLimpia === "" ? NaN : Number.parseFloat(distanciaLimpia);
    const distanciaNormalizada = Number.isNaN(distanciaNumero)
      ? NaN
      : Number(distanciaNumero.toFixed(2));

    if (Number.isNaN(distanciaNormalizada)) {
      nuevosErrores.distanciaKm = "Distancia inválida";
    }

    if (guardarComoPlantilla && modoCreacion === MODO_MANUAL && !nombrePlantilla.trim() && !form.nombreRuta.trim()) {
      nuevosErrores.nombrePlantilla = "Indique un nombre para la plantilla de ruta";
    }

    if (Object.keys(nuevosErrores).length > 0) {
      setErroresFormulario(nuevosErrores);
      setSaving(false);
      return;
    }

    setSaving(true);

    const payload = {
      cliente_id: form.clienteId.trim(),
      origen: form.origen.trim(),
      destino: form.destino.trim(),
      bultos_despachados: bultosDespachosValue,
      conductor_id: form.conductorId.trim(),
      camion_id: form.camionId.trim(),
      distancia_km: distanciaNormalizada,
    };
    if (form.nombreRuta?.trim()) {
      payload.nombre_ruta = form.nombreRuta.trim();
    }

    const fi = localDatetimeToIso(form.fechaInicio);
    const eta = localDatetimeToIso(form.eta);
    if (fi) payload.fecha_inicio = fi;
    if (eta) payload.eta = eta;

    const { inicio, fin, entrega } = form.fechasEstimadas;
    if (inicio?.trim() && fin?.trim() && entrega?.trim()) {
      payload.fecha_estimada_inicio = inicio.trim();
      payload.fecha_estimada_fin = fin.trim();
      payload.fecha_estimada_entrega = entrega.trim();
    }

    const paradasPayload = buildParadasPayload(paradas);
    if (paradasPayload.length > 0) {
      payload.paradas = paradasPayload;
    }
    if (modoCreacion === MODO_PLANTILLA && form.plantillaId?.trim()) {
      payload.ruta_plantilla_id = form.plantillaId.trim();
    }
    if (form.observaciones?.trim()) {
      payload.observaciones = form.observaciones.trim();
    }
    if (modoCreacion === MODO_MANUAL && guardarComoPlantilla) {
      payload.guardar_como_plantilla = true;
      payload.nombre_plantilla =
        nombrePlantilla.trim() || form.nombreRuta.trim() || undefined;
    }

    const resultado = await crearRuta(payload);
    setSaving(false);

    if (!resultado.success) {
      setMensaje({
        tipo: "error",
        texto: resultado.error || "No fue posible crear el pedido.",
      });
      return;
    }

    const pagoInfo = resultado.data?.pago;
    const textoOk = pagoInfo ? "Pedido creado. Pago pendiente." : "Pedido creado.";
    setMensaje({ tipo: "ok", texto: textoOk });

    const nuevaRutaId = resultado.data?.id;
    const clienteIdCreado = form.clienteId.trim();
    const plantillaIdCreada =
      modoCreacion === MODO_PLANTILLA ? form.plantillaId?.trim() : "";

    if (programarRecurrencia && nuevaRutaId && clienteIdCreado) {
      setRutaRecurrenciaCtx({
        clienteId: clienteIdCreado,
        rutaOrigenId: nuevaRutaId,
        rutaPlantillaId: plantillaIdCreada || undefined,
      });
      setRecurrenciaModalOpen(true);
    }

    setProgramarRecurrencia(false);
    setForm({
      nombreRuta: "",
      clienteId: "",
      plantillaId: "",
      conductorId: "",
      camionId: "",
      origen: "",
      destino: "",
      fechaInicio: "",
      eta: "",
      duracionMinutos: null,
      distanciaKm: "",
      fechasEstimadas: { ...FECHAS_VACIAS },
      advertenciaEstimacion: "",
      bultosDespachos: "",
      observaciones: "",
    });
    setParadas([]);
    setPlantillaSeleccionadaId("");
    setGuardarComoPlantilla(false);
    setNombrePlantilla("");
    setErroresFormulario({});
    setActiveTab("lista");
    await cargarRutas();
  };

  const actualizarFechaRuta = (rutaId, campo, valor) => {
    setFechasEdit((prev) => ({
      ...prev,
      [rutaId]: { ...(prev[rutaId] || fechasFromRuta({})), [campo]: valor },
    }));
  };

  const setMensajeRuta = (rutaId, scope, payload) => {
    setMensajesRuta((prev) => ({
      ...prev,
      [rutaId]: { ...(prev[rutaId] || {}), [scope]: payload },
    }));
  };

  const limpiarMensajeRuta = (rutaId, scope) => {
    setMensajesRuta((prev) => {
      const actual = { ...(prev[rutaId] || {}) };
      delete actual[scope];
      if (Object.keys(actual).length === 0) {
        const next = { ...prev };
        delete next[rutaId];
        return next;
      }
      return { ...prev, [rutaId]: actual };
    });
  };

  const guardarFechasRuta = async (rutaId) => {
    const f = fechasEdit[rutaId] || {};
    limpiarMensajeRuta(rutaId, "fechas");
    limpiarMensajeRuta(rutaId, "notificar");
    setSavingFechasId(rutaId);
    const body = {
      fecha_estimada_inicio: f.inicio,
      fecha_estimada_fin: f.fin,
      fecha_estimada_entrega: f.entrega,
    };
    if (String(f.distanciaKm ?? "").trim() !== "") {
      body.distancia_km = f.distanciaKm;
    }
    const res = await actualizarFechasEstimadas(rutaId, body);
    setSavingFechasId(null);
    if (!res.success) {
      setMensajeRuta(rutaId, "fechas", {
        tipo: "error",
        texto: res.error || "No se pudieron guardar las fechas estimadas.",
      });
      return;
    }
    setMensajeRuta(rutaId, "fechas", {
      tipo: "ok",
      texto: "Fechas estimadas guardadas.",
    });
    await cargarRutas();
  };

  const enviarNotificacionRuta = async (rutaId) => {
    limpiarMensajeRuta(rutaId, "notificar");
    limpiarMensajeRuta(rutaId, "fechas");
    setNotifyingId(rutaId);
    const res = await notificarFechaEstimada(rutaId);
    setNotifyingId(null);
    if (!res.success) {
      setMensajeRuta(rutaId, "notificar", {
        tipo: "error",
        texto: res.error || "No se pudo enviar la notificación.",
      });
      return;
    }
    const refEnvio = res.data?.messageId
      ? ` (ref. envío: ${res.data.messageId})`
      : "";
    setMensajeRuta(rutaId, "notificar", {
      tipo: "ok",
      texto:
        (res.data?.message || "Notificación enviada.") + refEnvio,
    });
    await cargarRutas();
  };

  useEffect(() => {
    let cancelled = false;

    async function cargarCostosDetalle() {
      const rutaId = rutaDetalleSeleccionada?.id;
      if (!rutaId) {
        setCostosDetalle(null);
        setLoadingCostosDetalle(false);
        return;
      }

      setLoadingCostosDetalle(true);
      const res = await obtenerCostosOperativos(rutaId);

      if (cancelled) return;

      if (res.error) {
        setCostosDetalle(null);
      } else {
        setCostosDetalle(res.data || null);
      }
      setLoadingCostosDetalle(false);
    }

    cargarCostosDetalle();

    return () => {
      cancelled = true;
    };
  }, [rutaDetalleSeleccionada?.id]);

  const rutasListaProcesadas = useMemo(
    () =>
      filtrarYOrdenarRutas(rutas, {
        search: listaSearch,
        filtroEstado: listaFiltroEstado,
        orden: listaOrden,
      }),
    [rutas, listaSearch, listaFiltroEstado, listaOrden],
  );

  const rutasPaginadas = useMemo(() => {
    const start = (listaPage - 1) * listaLimit;
    return rutasListaProcesadas.slice(start, start + listaLimit);
  }, [rutasListaProcesadas, listaPage, listaLimit]);

  const totalPagesLista = Math.max(
    1,
    Math.ceil(rutasListaProcesadas.length / listaLimit) || 1,
  );

  useEffect(() => {
    setListaPage(1);
  }, [listaSearch, listaFiltroEstado, listaOrden]);

  useEffect(() => {
    if (listaPage > totalPagesLista) {
      setListaPage(totalPagesLista);
    }
  }, [listaPage, totalPagesLista]);

  const formatCurrencyClp = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
      maximumFractionDigits: 0,
    });
  };

  const labelStyle = {
    fontSize: 12,
    color: "var(--lt-text-muted)",
    marginBottom: 4,
  };

  const valueStyle = {
    fontWeight: 600,
    color: "var(--lt-text-primary, #111827)",
    lineHeight: 1.35,
  };

  return (
    <div className="lt-module-inner">
      <div className="lt-card lt-module-card">
        <div className="lt-card__body">
        {mensaje?.tipo === "ok" && (
          <div className="lt-alert-banner lt-alert-banner--success" role="status">
            {mensaje.texto}
          </div>
        )}
        {mensaje?.tipo === "error" && (
          <div className="lt-alert-banner lt-alert-banner--error" role="alert">
            {mensaje.texto}
          </div>
        )}

        <div className="lt-tabs lt-tabs--module">
          <button
            type="button"
            className={`lt-tab ${activeTab === 'crear' ? 'lt-tab--active' : ''}`}
            onClick={() => setActiveTab('crear')}
          >
            Nuevo pedido
          </button>
          <button
            type="button"
            className={`lt-tab ${activeTab === 'lista' ? 'lt-tab--active' : ''}`}
            onClick={() => setActiveTab('lista')}
          >
            Pedidos registrados
          </button>
        </div>
        </div>
      </div>

      {activeTab === 'crear' && (
        <>
          <div className="lt-card lt-module-card">
            <div className="lt-card__body">
            <div className="lt-dashboard__map-filters">
              <button
                type="button"
                className={`lt-btn lt-btn--filter ${origenPedido === 'nuevo' ? 'lt-btn--filter-active' : ''}`}
                onClick={() => setOrigenPedido('nuevo')}
              >
                Pedido nuevo
              </button>
              {UI_FEATURES.repetirPedido ? (
                <button
                  type="button"
                  className={`lt-btn lt-btn--filter ${origenPedido === 'anterior' ? 'lt-btn--filter-active' : ''}`}
                  onClick={() => setOrigenPedido('anterior')}
                >
                  Usar pedido anterior
                </button>
              ) : null}
            </div>
            </div>
          </div>
          {origenPedido === 'nuevo' ? (
            <CreadorCarga
              onCreated={() => {
                cargarRutas();
                setActiveTab("lista");
              }}
            />
          ) : (
            <div className="lt-card lt-module-card">
              <div className="lt-empty">
                Función no disponible. Cree un pedido nuevo.
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'lista' && (
<div className="lt-card lt-module-card">
        <div className="lt-card__body">
        {loading ? (
          <Spinner message="Cargando pedidos…" />
        ) : rutas.length === 0 ? (
          <EmptyState
            title="Sin pedidos"
            description="No hay pedidos para mostrar. Cree uno en la pestaña «Nuevo pedido»."
          />
        ) : (
          <>
          <div className="lt-toolbar" style={{ marginBottom: 16 }}>
            <div className="lt-search-wrap">
              <Search size={16} className="lt-search-wrap__icon" />
              <input
                type="search"
                className="lt-input"
                placeholder="Buscar por pedido, cliente, conductor o ID…"
                value={listaSearch}
                onChange={(e) => setListaSearch(e.target.value)}
                aria-label="Buscar pedidos"
              />
            </div>
            <select
              className="lt-select"
              value={listaOrden}
              onChange={(e) => setListaOrden(e.target.value)}
              aria-label="Ordenar pedidos"
            >
              <option value={ORDEN_RECIENTES}>Más recientes</option>
              <option value={ORDEN_ANTIGUOS}>Más antiguos</option>
              <option value={ORDEN_NOMBRE_AZ}>Nombre A-Z</option>
              <option value={ORDEN_NOMBRE_ZA}>Nombre Z-A</option>
            </select>
          </div>
          <div className="lt-dashboard__map-filters" style={{ marginBottom: 16 }}>
            {[
              { key: FILTRO_LISTA_TODOS, label: "Todos" },
              { key: FILTRO_LISTA_PENDIENTES, label: "Pendientes" },
              { key: FILTRO_LISTA_ASIGNADOS, label: "Asignados" },
              { key: FILTRO_LISTA_EN_TRANSITO, label: "En tránsito" },
              { key: FILTRO_LISTA_ENTREGADOS, label: "Entregados" },
            ].map((f) => (
              <button
                key={f.key}
                type="button"
                className={`lt-btn lt-btn--filter ${listaFiltroEstado === f.key ? "lt-btn--filter-active" : ""}`}
                onClick={() => setListaFiltroEstado(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          {rutasListaProcesadas.length === 0 ? (
            <EmptyState
              title="Sin resultados"
              description="No hay pedidos que coincidan con la búsqueda o el filtro seleccionado."
            />
          ) : (
          <div className="lt-table-wrap">
            <table className="lt-table">
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Trayecto</th>
                  <th>Estado</th>
                  <th>Pago</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rutasPaginadas.map((ruta) => {
                  const monto = montoRutaDesdeListado(ruta);
                  return (
                  <React.Fragment key={ruta.id}>
                  <tr>
                    <td>
                      <strong>{getNombreRuta(ruta)}</strong>
                    </td>
                    <td className="lt-table__col--truncate">{truncarTexto(ruta.clientes?.nombre, 28)}</td>
                    <td className="lt-table__col--truncate">
                      <div>{truncarTexto(ruta.origen, 32)}</div>
                      <div className="lt-table__cell-sub">
                        → {truncarTexto(ruta.destino, 32)}
                      </div>
                    </td>
                    <td>
                      <Badge variant={estadoBadgeVariant(ruta.estado)} showDot={false}>
                        {estadoLabel(ruta.estado)}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant={estadoPagoBadgeVariant(ruta.estado_pago)} showDot={false}>
                        {estadoLabel(ruta.estado_pago || "PENDIENTE")}
                      </Badge>
                      {monto != null && (
                        <div className="lt-table__cell-sub">
                          {formatCurrencyClp(monto)}
                        </div>
                      )}
                      {ruta.estado_pago === "pagado" && (
                        <button
                          type="button"
                          onClick={() => setComprobanteRutaId(ruta.id)}
                          className="lt-btn lt-btn--secondary lt-btn--sm"
                          style={{ marginTop: 6 }}
                        >
                          Comprobante
                        </button>
                      )}
                    </td>
                    <td>
                      <div className="lt-table__actions">
                      <button
                        type="button"
                        className="lt-btn lt-btn--secondary lt-btn--sm"
                        onClick={() =>
                          setRutaDetalleSeleccionada({
                            ...(rutas.find((r) => r.id === ruta.id) || ruta),
                            anomalias:
                              Array.isArray(ruta?.anomalias) && ruta.anomalias.length > 0
                                ? ruta.anomalias
                                : Array.isArray(anomaliasPorRuta[ruta.id])
                                  ? anomaliasPorRuta[ruta.id]
                                  : [],
                          })
                        }
                      >
                        Ver detalle
                      </button>
                      {UI_FEATURES.consolidacion ? (
                      <button
                        type="button"
                        className={`lt-btn lt-btn--sm ${consolidacionAbiertaId === ruta.id ? "lt-btn--primary" : "lt-btn--secondary"}`}
                        onClick={() =>
                          setConsolidacionAbiertaId((prev) =>
                            prev === ruta.id ? null : ruta.id,
                          )
                        }
                        disabled={!ruta.camion_id}
                        title={
                          ruta.camion_id
                            ? "Gestionar consolidación de pedidos"
                            : "Asigne un camión para consolidar pedidos"
                        }
                      >
                        {consolidacionAbiertaId === ruta.id ? "Cerrar consolidación" : "Consolidar"}
                      </button>
                      ) : null}
                      <button
                        type="button"
                        className={`lt-btn lt-btn--sm ${costosAbiertoId === ruta.id ? "lt-btn--primary" : "lt-btn--secondary"}`}
                        onClick={() =>
                          setCostosAbiertoId((prev) =>
                            prev === ruta.id ? null : ruta.id,
                          )
                        }
                      >
                        {costosAbiertoId === ruta.id ? "Cerrar costos" : "Ver costos"}
                      </button>
                      {UI_FEATURES.repetirPedido && (
                      <button
                        type="button"
                        className="lt-btn lt-btn--secondary lt-btn--sm"
                        onClick={() => {
                          setRutaRecurrenciaCtx({
                            clienteId: ruta.cliente_id,
                            rutaOrigenId: ruta.id,
                            rutaPlantillaId: ruta.ruta_plantilla_id || undefined,
                          });
                          setRecurrenciaModalOpen(true);
                        }}
                      >
                        Usar pedido anterior
                      </button>
                      )}
                      <button
                        type="button"
                        className="lt-btn lt-btn--danger lt-btn--sm"
                        onClick={() => handleDelete(ruta.id)}
                      >
                        Eliminar
                      </button>
                      </div>
                    </td>
                  </tr>
                  {UI_FEATURES.consolidacion && consolidacionAbiertaId === ruta.id && (
                    <tr key={`${ruta.id}-consolidacion`}>
                      <td colSpan={COLS_TABLA_RUTAS} className="lt-consolidacion-row">
                        <ConsolidacionRutaPanel
                          rutaId={ruta.id}
                          onConsolidado={cargarRutas}
                        />
                      </td>
                    </tr>
                  )}
                  {costosAbiertoId === ruta.id && (
                    <tr key={`${ruta.id}-costos`}>
                      <td colSpan={COLS_TABLA_RUTAS} className="lt-consolidacion-row">
                        <CostosOperativosPanel
                          rutaId={ruta.id}
                          rutaEstado={ruta.estado}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <Pagination
              currentPage={listaPage}
              totalPages={totalPagesLista}
              totalItems={rutasListaProcesadas.length}
              limit={listaLimit}
              onPageChange={setListaPage}
              onLimitChange={(newLimit) => {
                setListaLimit(newLimit);
                setListaPage(1);
              }}
            />
          </div>
          )}
          </>
        )}
        </div>
      </div>
      )}

      {UI_FEATURES.repetirPedido ? (
      <ModalRecurrencia
        open={recurrenciaModalOpen}
        onClose={() => {
          setRecurrenciaModalOpen(false);
          setRutaRecurrenciaCtx(null);
        }}
        onSuccess={() => {
          setMensaje({
            tipo: "ok",
            texto: "Recurrencia configurada.",
          });
        }}
        clienteId={rutaRecurrenciaCtx?.clienteId}
        rutaOrigenId={rutaRecurrenciaCtx?.rutaOrigenId}
        rutaPlantillaId={rutaRecurrenciaCtx?.rutaPlantillaId}
        titulo="Usar pedido anterior"
      />
      ) : null}

      {rutaDetalleSeleccionada && (
        <div
          className="lt-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ruta-detalle-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setRutaDetalleSeleccionada(null);
          }}
        >
          <div
            className="lt-modal-dialog lt-modal-dialog--lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lt-modal-header">
              <div>
                <div className="lt-modal-header__title" id="ruta-detalle-title">
                  Detalle del pedido
                </div>
                <div className="lt-modal-header__sub">
                  {getNombreRuta(rutaDetalleSeleccionada)}
                </div>
              </div>
              <button
                type="button"
                className="lt-modal-close"
                aria-label="Cerrar"
                onClick={() => setRutaDetalleSeleccionada(null)}
              >
                ×
              </button>
            </div>

            <div className="lt-modal-body">
              <div className="lt-form-grid" style={{ gap: 14, gridTemplateColumns: "1fr" }}>
                <div className="lt-card" style={{ padding: 16 }}>
                  <div className="lt-list-item__title" style={{ marginBottom: 8, color: "var(--lt-success)" }}>
                    Información del Viaje
                  </div>
                  <div style={labelStyle}>Cliente</div>
                  <div style={valueStyle}>{rutaDetalleSeleccionada.clientes?.nombre || "—"}</div>
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 12 }}>
                    <div style={{ minWidth: 140, flex: "1 1 180px" }}>
                      <div style={labelStyle}>Origen</div>
                      <div style={valueStyle}>{rutaDetalleSeleccionada.origen || "—"}</div>
                    </div>
                    <div style={{ minWidth: 140, flex: "1 1 180px" }}>
                      <div style={labelStyle}>Destino</div>
                      <div style={valueStyle}>{rutaDetalleSeleccionada.destino || "—"}</div>
                    </div>
                    <div style={{ minWidth: 120, flex: "1 1 150px" }}>
                      <div style={labelStyle}>Distancia</div>
                      <div style={valueStyle}>
                        {rutaDetalleSeleccionada.distancia_km != null &&
                        String(rutaDetalleSeleccionada.distancia_km).trim() !== ""
                          ? `${rutaDetalleSeleccionada.distancia_km} km`
                          : "—"}
                      </div>
                    </div>
                    <div style={{ minWidth: 120, flex: "1 1 140px" }}>
                      <div style={labelStyle}>Slots utilizados</div>
                      <div style={valueStyle}>
                        {rutaDetalleSeleccionada.bultos ??
                          rutaDetalleSeleccionada.bultos_despachados ??
                          "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lt-card" style={{ padding: 16 }}>
                  <div className="lt-list-item__title" style={{ marginBottom: 8, color: "var(--lt-success)" }}>
                    Logística
                  </div>
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 160, flex: "1 1 200px" }}>
                      <div style={labelStyle}>Conductor</div>
                      <div style={valueStyle}>{rutaDetalleSeleccionada.conductores?.rut || "—"}</div>
                    </div>
                    <div style={{ minWidth: 160, flex: "1 1 200px" }}>
                      <div style={labelStyle}>Camión</div>
                      <div style={valueStyle}>{rutaDetalleSeleccionada.camiones?.patente || "—"}</div>
                    </div>
                    <div style={{ minWidth: 160, flex: "1 1 200px" }}>
                      <div style={labelStyle}>Estado actual</div>
                      <div>
                        <Badge variant={estadoBadgeVariant(rutaDetalleSeleccionada.estado)}>
                          {estadoLabel(rutaDetalleSeleccionada.estado)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lt-card" style={{ padding: 16 }}>
                  <div className="lt-list-item__title" style={{ marginBottom: 8, color: "var(--lt-success)" }}>
                    Tiempos
                  </div>
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 10 }}>
                    <div style={{ minWidth: 180, flex: "1 1 220px" }}>
                      <div style={labelStyle}>Fecha de inicio</div>
                      <div style={valueStyle}>{fmtDate(rutaDetalleSeleccionada.fecha_inicio)}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 12 }}>
                    <div style={{ minWidth: 160, flex: "1 1 200px" }}>
                      <div style={labelStyle}>Inicio rango</div>
                      <div style={valueStyle}>{toInputDate(rutaDetalleSeleccionada.fecha_estimada_inicio) || "—"}</div>
                    </div>
                    <div style={{ minWidth: 160, flex: "1 1 200px" }}>
                      <div style={labelStyle}>Fin rango</div>
                      <div style={valueStyle}>{toInputDate(rutaDetalleSeleccionada.fecha_estimada_fin) || "—"}</div>
                    </div>
                    <div style={{ minWidth: 160, flex: "1 1 200px" }}>
                      <div style={labelStyle}>Día estimado</div>
                      <div style={valueStyle}>{toInputDate(rutaDetalleSeleccionada.fecha_estimada_entrega) || "—"}</div>
                    </div>
                  </div>
                  {(() => {
                    const rutaId = rutaDetalleSeleccionada.id;
                    const rutaRef = rutas.find((r) => r.id === rutaId) || rutaDetalleSeleccionada;
                    return (
                      <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 12 }}>
                        <div className="lt-list-item__title" style={{ marginBottom: 8, fontSize: 13 }}>
                          Gestionar fechas estimadas
                        </div>
                        <button
                          type="button"
                          className="lt-btn lt-btn--secondary lt-btn--sm"
                          style={{ marginBottom: 8, width: "100%" }}
                          disabled={calculandoEstimacionRutaId === rutaId}
                          onClick={() => calcularDistanciaYFechasRuta(rutaRef)}
                        >
                          {calculandoEstimacionRutaId === rutaId
                            ? "Calculando…"
                            : "Calcular fechas"}
                        </button>
                        <MensajeFilaRuta mensaje={mensajesRuta[rutaId]?.estimacion} />
                        <div className="lt-field-group">
                          <label className="lt-label" htmlFor={`fecha-entrega-detalle-${rutaId}`}>
                            Día estimado de entrega
                          </label>
                          <input
                            id={`fecha-entrega-detalle-${rutaId}`}
                            type="date"
                            className="lt-input"
                            value={(fechasEdit[rutaId] || fechasFromRuta(rutaRef)).entrega}
                            onChange={(e) =>
                              actualizarFechaRuta(rutaId, "entrega", e.target.value)
                            }
                          />
                        </div>
                        <button
                          type="button"
                          className="lt-btn lt-btn--secondary lt-btn--sm"
                          style={{ marginTop: 8, width: "100%" }}
                          disabled={savingFechasId === rutaId}
                          onClick={() => guardarFechasRuta(rutaId)}
                        >
                          {savingFechasId === rutaId ? "Guardando…" : "Guardar fechas"}
                        </button>
                        <MensajeFilaRuta mensaje={mensajesRuta[rutaId]?.fechas} />
                        <button
                          type="button"
                          className="lt-btn lt-btn--success lt-btn--sm"
                          style={{ marginTop: 8, width: "100%" }}
                          disabled={notifyingId === rutaId}
                          onClick={() => enviarNotificacionRuta(rutaId)}
                        >
                          {notifyingId === rutaId
                            ? "Enviando…"
                            : "Notificar fecha estimada"}
                        </button>
                        <MensajeFilaRuta mensaje={mensajesRuta[rutaId]?.notificar} />
                      </div>
                    );
                  })()}
                </div>

                <div className="lt-card" style={{ padding: 16 }}>
                  <div className="lt-list-item__title" style={{ marginBottom: 8, color: "var(--lt-success)" }}>
                    Costos operativos
                  </div>
                  {loadingCostosDetalle ? (
                    <p className="lt-list-item__sub" style={{ margin: 0 }}>
                      Calculando costos operativos...
                    </p>
                  ) : costosDetalle && (
                    costosDetalle.total != null ||
                    costosDetalle.total_pedido != null ||
                    costosDetalle.costoTotal != null ||
                    costosDetalle.combustible != null ||
                    costosDetalle.costoCombustible != null ||
                    costosDetalle.conductor != null ||
                    costosDetalle.costoConductor != null ||
                    costosDetalle.espera != null ||
                    costosDetalle.costoEspera != null ||
                    costosDetalle.tiempoEsperaMinutos != null ||
                    costosDetalle.peajes != null ||
                    costosDetalle.costoPeajes != null ||
                    costosDetalle.precioCombustibleLitro != null
                  ) ? (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 12,
                        paddingBottom: 4,
                      }}
                    >
                      <div className="lt-card" style={{ padding: 14, minWidth: 180, flex: "0 0 180px" }}>
                        <div className="lt-card__subtitle" style={{ marginBottom: 6 }}>
                          Combustible
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--lt-success)" }}>
                          {formatCurrencyClp(costosDetalle.combustible ?? costosDetalle.costoCombustible)}
                        </div>
                        <div className="lt-card__subtitle" style={{ marginTop: 4 }}>
                          {`${formatCurrencyClp(costosDetalle.precioCombustibleLitro ?? 1200)}/L`}
                        </div>
                      </div>

                      <div className="lt-card" style={{ padding: 14, minWidth: 180, flex: "0 0 180px" }}>
                        <div className="lt-card__subtitle" style={{ marginBottom: 6 }}>
                          Conductor
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--lt-success)" }}>
                          {formatCurrencyClp(costosDetalle.conductor ?? costosDetalle.costoConductor)}
                        </div>
                      </div>

                      <div className="lt-card" style={{ padding: 14, minWidth: 180, flex: "0 0 180px" }}>
                        <div className="lt-card__subtitle" style={{ marginBottom: 6 }}>
                          Espera
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--lt-success)" }}>
                          {formatCurrencyClp(costosDetalle.espera ?? costosDetalle.costoEspera)}
                        </div>
                        <div className="lt-card__subtitle" style={{ marginTop: 4 }}>
                          {`${costosDetalle.tiempoEsperaMinutos ?? 0} min`}
                        </div>
                      </div>

                      <div className="lt-card" style={{ padding: 14, minWidth: 180, flex: "0 0 180px" }}>
                        <div className="lt-card__subtitle" style={{ marginBottom: 6 }}>
                          Peajes
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--lt-success)" }}>
                          {formatCurrencyClp(costosDetalle.peajes ?? costosDetalle.costoPeajes)}
                        </div>
                      </div>

                      <div className="lt-card" style={{ padding: 14, minWidth: 180, flex: "0 0 180px" }}>
                        <div className="lt-card__subtitle" style={{ marginBottom: 6 }}>
                          Total pedido
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--lt-success)" }}>
                          {formatCurrencyClp(
                            costosDetalle.total ??
                              costosDetalle.total_pedido ??
                              costosDetalle.costoTotal,
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="lt-list-item__sub" style={{ margin: 0 }}>
                      Sin costos operativos registrados
                    </p>
                  )}
                </div>

                <div className="lt-card" style={{ padding: 16 }}>
                  <div className="lt-list-item__title" style={{ marginBottom: 8, color: "var(--lt-success)" }}>
                    Información de pago
                  </div>
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 12 }}>
                    <div style={{ minWidth: 160, flex: "1 1 180px" }}>
                      <div style={labelStyle}>Estado</div>
                      <Badge variant={estadoPagoBadgeVariant(rutaDetalleSeleccionada.estado_pago)}>
                        {estadoLabel(rutaDetalleSeleccionada.estado_pago || "PENDIENTE")}
                      </Badge>
                    </div>
                    <div style={{ minWidth: 160, flex: "1 1 180px" }}>
                      <div style={labelStyle}>Monto</div>
                      <div style={valueStyle}>
                        {montoRutaDesdeListado(rutaDetalleSeleccionada) != null
                          ? formatCurrencyClp(montoRutaDesdeListado(rutaDetalleSeleccionada))
                          : "—"}
                      </div>
                    </div>
                  </div>
                  {montoRutaDesdeListado(rutaDetalleSeleccionada) != null && (
                    <button
                      type="button"
                      className="lt-btn lt-btn--secondary lt-btn--sm"
                      onClick={() => {
                        setRutaRentabilidad({
                          ...rutaDetalleSeleccionada,
                          ...computeRentabilidadRuta(rutaDetalleSeleccionada),
                        });
                        setShowRentabilidadModal(true);
                      }}
                    >
                      Ver resumen de rentabilidad
                    </button>
                  )}
                  {rutaDetalleSeleccionada.estado_pago === "pagado" && (
                    <button
                      type="button"
                      className="lt-btn lt-btn--secondary lt-btn--sm"
                      style={{ marginLeft: 8 }}
                      onClick={() => setComprobanteRutaId(rutaDetalleSeleccionada.id)}
                    >
                      Ver comprobante
                    </button>
                  )}
                </div>

              </div>

              <div className="lt-modal-footer">
                <button
                  type="button"
                  className="lt-btn lt-btn--secondary"
                  onClick={() => setRutaDetalleSeleccionada(null)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {comprobanteRutaId ? (
        <ComprobanteModal
          rutaId={comprobanteRutaId}
          onClose={() => setComprobanteRutaId(null)}
        />
      ) : null}
      {showRentabilidadModal && rutaRentabilidad ? (
        <div
          className="lt-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowRentabilidadModal(false);
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="lt-modal-dialog lt-modal-dialog--sm">
            <div className="lt-modal-header">
              <div>
                <div className="lt-modal-header__title">Simulador de rentabilidad interna</div>
                <div className="lt-modal-header__sub">Resumen financiero del pedido</div>
              </div>
              <button
                type="button"
                className="lt-modal-close"
                onClick={() => setShowRentabilidadModal(false)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="lt-modal-body">
              <div className="lt-modal-detail-grid">
                <div className="lt-modal-detail-row">
                  <span className="lt-modal-detail-row__label">Total recaudado</span>
                  <span className="lt-modal-detail-row__value">
                    {new Intl.NumberFormat("es-CL", {
                      style: "currency",
                      currency: "CLP",
                      maximumFractionDigits: 0,
                    }).format(rutaRentabilidad.totalCobradoBruto)}
                  </span>
                </div>
                <div className="lt-modal-detail-row">
                  <span className="lt-modal-detail-row__label">Gastos operativos</span>
                  <span className="lt-modal-detail-row__value">
                    {new Intl.NumberFormat("es-CL", {
                      style: "currency",
                      currency: "CLP",
                      maximumFractionDigits: 0,
                    }).format(rutaRentabilidad.gastos)}
                  </span>
                </div>
                <div className="lt-modal-detail-row">
                  <span className="lt-modal-detail-row__label">Ganancia neta</span>
                  <span className="lt-modal-detail-row__value lt-modal-detail-row__value--positive">
                    {new Intl.NumberFormat("es-CL", {
                      style: "currency",
                      currency: "CLP",
                      maximumFractionDigits: 0,
                    }).format(rutaRentabilidad.ganancia)}
                  </span>
                </div>
              </div>
            </div>
            <div className="lt-modal-footer">
              <button
                type="button"
                className="lt-btn lt-btn--primary"
                onClick={() => setShowRentabilidadModal(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
